/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description React Hook useUploadAttachments
 */
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import api from 'client/index';
import AppEvents from '@veritable/common/utils/appEvents';
import QUERY_KEYS from 'app-constants/queryKeys';

interface IAttachmentFile {
	fileName: string;
	fileType: string;
}

interface IAttachmentUploadPayload {
	files: IAttachmentFile[];
	description?: string;
}

interface UploadAttachmentProps {
	entityType: EntityTypeEnum;
	entityId: string;
	files: IAttachmentUploadPayload['files'];
	description?: string;
}

const useUploadAttachments = (): UseMutationResult<PresignedUrlResponse[], IAxiosError, UploadAttachmentProps, unknown> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ entityType, entityId, files, description }) => {
			const payload: IAttachmentUploadPayload = {
				files,
				...(description ? { description } : {}),
			};

			const { data } = await api.AttachmentApi.uploadEntityAttachments({
				entityType,
				entityId,
				uploadMultipleFiles: payload,
			});
			return data;
		},
		onSuccess: (_, { entityType, entityId }) => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allAttachments, { id: entityId, entityType }] });
		},
		onError: (error) => {
			AppEvents.publishApiError({ error });
		},
	});
};

export default useUploadAttachments;
